import React from 'react';
import * as assetActions from '../../../redux/actions/assetsAction';
import { useDispatch, useSelector } from 'react-redux';
import Papa from 'papaparse';
import { productCSVFileFields } from '../../../Constants/Asset';
import { openErrorSnackBar } from '../../../redux/actions/snackBarActions';
import TabHeader from '../../common/TabHeader';
import CustomTempDrawer from '../../common/CustomTempDrawer';
import { Button, Tooltip, makeStyles } from '@material-ui/core';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import DownloadIcon from '@material-ui/icons/GetApp';
import ClearIcon from '@material-ui/icons/Clear';
import { csvFileRowLimit } from '../../../Constants/Files';

const title = 'Upload CSV';
const clearCSVDataHeader = 'Clear CSV';
const uploadCSVBtn = title;
const explanation = `Assets created via an uploaded CSV will use the combination of IVM fields to automatically create assets. Any relevant information will be added per asset,
  but can be edited in the user interface below. Use the "Next" button to continue to the "Add Details" section or skip directly to the "Review & Create" section to complete asset creation. The uploaded CSV has a max limit of ${csvFileRowLimit} rows.`;
const uploadCSVBtnTooltip = 'Click to upload a CSV file that can be used to generate assets. ';
const clearCSVDataBtnTooltip = 'Click to clear CSV data';
const shotflowBtnLabel = 'Download Shotflow CSV';
const shotflowBtnData = 'download-shotflow-csv-btn';
const shotflowBtnHref = '/shotflow-csv.csv';
const sampleCSVBtnLabel = 'Download Sample CSV';
const sampleCSVBtnHref = '/must-have-csv.csv';
const sampleCSVBtnData = 'download-must-have-csv-btn';

const useStyles = makeStyles((theme) => ({
	hidden: { display: 'none' },
}));

const RenderDownloadButton = ({ data, href, label }) => {
	return (
		<Button color="secondary" data-cy={data} startIcon={<DownloadIcon />} href={process.env.PUBLIC_URL + href}>
			{label}
		</Button>
	);
};

const AssetCSVUpload = () => {
	const classes = useStyles();
	const assets = useSelector((state) => state.assets);
	const dispatch = useDispatch();

	// Component which renders upload CSV button.
	const UploadCsvBtn = () => {
		return (
			<Tooltip title={uploadCSVBtnTooltip} arrow={true} placement="top">
				<Button
					variant="contained"
					color="primary"
					component="label"
					data-cy="upload-csv-btn"
					startIcon={<InsertDriveFileOutlinedIcon />}
				>
					<input
						type="file"
						accept=".csv"
						data-cy="upload-csv-input"
						className={classes.hidden}
						onChange={handleFileOnChange}
					></input>
					{uploadCSVBtn}
				</Button>
			</Tooltip>
		);
	};
	// Component to render clear CSV data Btn.
	const ClearCSVDataBtn = () => {
		const dispatch = useDispatch();
		const handleDrawerBtnClick = () => {
			dispatch(assetActions.resetAssetData());
		};
		// clear CSV data warning Drawer states and props.
		const [openDrawer, setDrawerOpen] = React.useState(false);
		//  clear CSV data warning Drawer props
		const tempDrawerProps = {
			open: openDrawer,
			setOpen: setDrawerOpen,
			header: 'CLEAR CSV DATA',
			confirmationMessage:
				'Are you sure you want to clear? Any assets that you have started to create will not be saved and you will lose all progress.',
			primaryBtnStartIcon: <ClearIcon />,
			primaryBtnTxt: 'Yes,Clear CSV Data',
			primaryButtonClick: handleDrawerBtnClick,
		};
		const handleClearCSVBtnClick = () => {
			setDrawerOpen(true);
		};
		return (
			<>
				<Tooltip title={clearCSVDataBtnTooltip} arrow={true} placement="top">
					<Button
						variant="contained"
						color="primary"
						size="small"
						component="label"
						data-cy="clear-csv-btn"
						startIcon={<ClearIcon />}
						onClick={handleClearCSVBtnClick}
					>
						{clearCSVDataHeader}
					</Button>
				</Tooltip>
				<CustomTempDrawer {...tempDrawerProps} />
			</>
		);
	};
	const RenderUploadCSVOrClearCSVBtn = () => {
		// load clear CSV btn when there is a CSV data work in progress.
		// to prevent user to upload another CSV.
		if (assets.length === 0) {
			return <UploadCsvBtn />;
		} else {
			return <ClearCSVDataBtn />;
		}
	};
	const processCSVData = (csvData) => {
		if (csvData.length > csvFileRowLimit) {
			dispatch(
				openErrorSnackBar(
					`CSV file has more than ${csvFileRowLimit} rows of data, please upload another CSV which has less than ${csvFileRowLimit} rows.`,
				),
			);
		} else {
			const bulkSearchRequestCollection = [];
			csvData.forEach((item) => {
				// avoid processing empty rows.
				const itemIsEmpty = !Object.values(item).some((x) => x !== null && x !== '');
				if (!itemIsEmpty) {
					const hasIVMFields =
						productCSVFileFields.itemNumber in item &&
						productCSVFileFields.vendorNumber in item &&
						productCSVFileFields.modelNumber in item;

					// only send to backend if a record has IVM fields.
					if (hasIVMFields) {
						const ivmObj = {
							item: item[productCSVFileFields.itemNumber],
							vendor: item[productCSVFileFields.vendorNumber],
							model: item[productCSVFileFields.modelNumber],
						};
						bulkSearchRequestCollection.push(ivmObj);
					} else {
						const errorMsg = 'Error processing CSV file. Item, Vendor, Model fields are missing';
						console.log(`${errorMsg} in item ${JSON.stringify(item)}`);
						dispatch(openErrorSnackBar(errorMsg));
					}
				} else {
					dispatch(openErrorSnackBar('CSV file is empty'));
				}
			});

			// only make an API call if we have at least one row with IVM fields.
			if (bulkSearchRequestCollection.length > 0) {
				dispatch(assetActions.processCSVData(bulkSearchRequestCollection));
			}
		}
	};
	// adding files to manage asset state
	const handleFileOnChange = (event) => {
		const file = event.target.files[0];
		// resetting upload file input value.
		event.target.value = null;
		// Parsing uploaded CSV file using Papaparse JS CSV parser.
		// Keys data by field name rather than an array.
		Papa.parse(file, {
			header: true,
			complete: function (results) {
				const csvData = results.data;
				processCSVData(csvData);
			},
			skipEmptyLines: true,
			error: function (error, file) {
				console.log(`failed parsing file ${file.name} with error ${error}`);
				dispatch(openErrorSnackBar('Invalid CSV File. Please try to upload again with a valid CSV file.'));
			},
		});
	};
	const actions = [
		<RenderUploadCSVOrClearCSVBtn />,
		<RenderDownloadButton label={shotflowBtnLabel} href={shotflowBtnHref} data={shotflowBtnData} />,
		<RenderDownloadButton label={sampleCSVBtnLabel} href={sampleCSVBtnHref} data={sampleCSVBtnData} />,
	];
	return (
		<TabHeader
			title={title}
			explanation={explanation}
			explanationDataCy="upload-files-content-msg"
			actions={actions}
		/>
	);
};
export default AssetCSVUpload;
